import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import { usePageTracking } from './usePageTracking'; // Import the hook
import ContactUs from "./components/ContactUs/ContactUs";
import ForgotPassword from "./components/Auth/ForgotPassword";
import ErrorBoundary from "./components/ErrorHandling/ErrorBoundary";
import Notifications from "./components/Notifications/Notifications";

const Home = lazy(() => import("./components/Home/Home"));
const About = lazy(() => import("./components/About/About"));
const Projects = lazy(() => import("./components/Projects/Projects"));
const Resume = lazy(() => import("./components/Resume/Resume"));
const AuthDetails = lazy(() => import("./components/Auth/AuthDetails"));
const GitHubMetrics = lazy(() => import("./components/Star/GitHub/GitHubMetrics"));
const LeetCodeView = lazy(() => import("./components/Star/LeetCode/LeetCodeView"));
const Certificates = lazy(() => import("./components/Certificates/Certificates"));

// Each game is a whole route of its own, so it has no business in the main
// bundle. These were eager imports, which defeated the code-splitting the rest
// of this file does.
const BlockStacker = lazy(() => import("./components/About/BlockStacker"));
const SnakeGame = lazy(() => import("./components/About/SnakeGame"));
const SpaceRaiders = lazy(() => import("./components/About/SpaceRaiders"));
const FruitNinjaTechnologiesGame = lazy(() => import("./components/About/FruitNinjaTechnologiesGame"));

const PinterestPrivacyPolicy = lazy(() => import("./components/Pinterest/PinterestPrivacyPolicy"));

// Create a new component for your main app logic to ensure the hook is used inside the Router
function MainApp() {
  // Call the usePageTracking hook here inside the Router context
  usePageTracking();

  return (
    <>
      <ErrorBoundary title="Navigation unavailable" message="The menu failed to load.">
        <Navbar />
      </ErrorBoundary>
      <ScrollToTop />
      <div className="main-content">
        <ErrorBoundary
          title="This page failed to load"
          message="Something went wrong rendering this section. Try again, or head back home."
        >
          <Suspense fallback={<div>Loading...</div>}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/project" element={<Projects />} />
              <Route path="/about" element={<About />} />
              <Route path="/resume" element={<Resume />} />
              <Route path="/contact" element={<ContactUs />} />
              <Route path="/secret" element={<AuthDetails />} />
              <Route path="/github" element={<GitHubMetrics />} />
              <Route path="/leetcode" element={<LeetCodeView />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/certificates" element={<Certificates />} />
              <Route path="/blockstacker" element = {<BlockStacker/>} />
              <Route path="/snake-game" element = {<SnakeGame/>} />
              <Route path="/space-raiders" element = {<SpaceRaiders/>} />
              <Route path="/fruit-ninja" element = {<FruitNinjaTechnologiesGame/>} />
              <Route path="/pinterest-privacy-policy" element={<PinterestPrivacyPolicy/>} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </div>
      <ErrorBoundary title="Footer unavailable" message="The footer failed to load.">
        <Footer />
      </ErrorBoundary>
      <Notifications />
    </>
  );
}

function App() {
  // There used to be a 1200ms setTimeout here gating the whole app behind
  // <Preloader />, which rendered an empty <div id="preloader"> that no
  // stylesheet ever targeted — no spinner, no overlay, just a blank 1.2s.
  // The route-level Suspense fallback already covers the real wait.
  return (
    <Router basename="/">
      <div className="App portfolio-shell">
        {/* The main app logic inside Router */}
        <MainApp />
      </div>
    </Router>
  );
}

export default App;
